"use client";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { useState } from "react";
import { FaRegLightbulb, FaTools, FaCashRegister } from "react-icons/fa";
import { FaMotorcycle } from "react-icons/fa6";

import Illustration from "@/public/images/glow-top.svg";

import ButtonFreeTrial from "./button-free-trial";

const icons = {
  1: FaRegLightbulb,
  2: FaTools,
  3: FaCashRegister,
  4: FaMotorcycle,
};

export default function Features() {
  const [tab, setTab] = useState(1);

  const tabsData = [
    {
      id: 1,
      title: "Uma Solução Feita para Você",
      description:
        "A Rota Lounge foi criada para resolver problemas reais do seu dia a dia. Simplifique processos, economize tempo e impulsione suas vendas com funcionalidades que realmente importam.",
      buttonText: "Solução Específica e Inovadora",
    },
    {
      id: 2,
      title: "Múltiplas Funcionalidades",
      description:
        "Controle de estoque completo, gestão de produtos individuais ou combos, vendas rápidas no balcão (PDV), geração simples de comandas, delivery ágil com página própria para receber pedidos diretamente no sistema, e muito mais.",
      buttonText: "Múltiplas Funcionalidades",
    },
    {
      id: 3,
      title: "Vendas PDV",
      description:
        "Realize vendas avulsas no balcão de forma rápida, simples e eficiente, garantindo uma ótima experiência para seus clientes",
      buttonText: "Vendas PDV",
    },
    {
      id: 4,
      title: "Delivery",
      description:
        "Gerencie seus pedidos e entregadores com eficiência e receba pedidos diretamente pela sua própria página online, simplificando o delivery e garantindo agilidade para você e seus clientes.",
      buttonText: "Delivery",
    },
  ];

  const renderTabContent = () => {
    const currentTab = tabsData.find((tabData) => tabData.id === tab);
    if (!currentTab) return null;

    const initial = { opacity: 0, y: -20 };
    const animate = { opacity: 1, y: 0 };
    const transition = { duration: 0.5, ease: "easeInOut" };

    return (
      <motion.div key={currentTab.id} initial={initial} animate={animate} transition={transition}>
        <h3 className='h3 bg-clip-text text-transparent bg-gradient-to-r from-slate-200/60 via-slate-200 to-slate-200/60 pb-3'>
          {currentTab.title}
        </h3>
        <p className='text-lg text-slate-400 mb-8'>{currentTab.description}</p>
      </motion.div>
    );
  };

  return (
    <section id='about'>
      <div className='relative max-w-6xl mx-auto px-4 sm:px-6'>
        {/* Illustration */}
        <div
          className='absolute inset-0 -z-10 -mx-28 rounded-t-[3rem] pointer-events-none overflow-hidden'
          aria-hidden='true'
        >
          <div className='absolute left-1/2 -translate-x-1/2 top-0 -z-10'>
            <Image
              src={Illustration}
              className='max-w-none'
              width={1404}
              height={658}
              alt='Features Illustration'
              unoptimized={true}
            />
          </div>
        </div>

        <div className='pt-16 pb-12 md:pt-52 md:pb-20'>
          <div>
            {/* Section content */}
            <div className='max-w-xl mx-auto md:max-w-none flex flex-col md:flex-row md:space-x-8 lg:space-x-16 xl:space-x-20 space-y-8 space-y-reverse md:space-y-0'>
              {/* Conteúdo que varia de acordo com a aba */}
              <div className='md:w-7/12 lg:w-1/2 order-1 md:order-none max-md:text-center' data-aos='fade-down'>
                <div>{renderTabContent()}</div>
                <div className='mt-8 max-w-xs max-md:mx-auto space-y-2'>
                  {tabsData.map((tabData) => (
                    <button
                      key={tabData.id}
                      className={`flex items-center text-sm font-medium text-slate-50 rounded border bg-slate-800/25 w-full px-3 py-2 transition duration-150 ease-in-out hover:opacity-100 ${tab !== tabData.id ? "border-slate-700 opacity-50" : "border-purple-700 shadow shadow-purple-500/25"}`}
                      onClick={() => setTab(tabData.id)}
                    >
                      <span>{tabData.buttonText}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Image */}
              <div className='md:w-5/12 lg:w-1/2' data-aos='fade-up' data-aos-delay='100'>
                <div className='relative pt-24 -mt-12'>
                  <div className='flex items-center justify-center'>
                    <div className='relative w-48 h-48 flex justify-center items-center'>
                      {/* Halo effect */}
                      <svg
                        className='absolute inset-0 left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 will-change-transform pointer-events-none blur-md'
                        width='480'
                        height='480'
                        viewBox='0 0 480 480'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <defs>
                          <linearGradient id='pulse-a' x1='50%' x2='50%' y1='100%' y2='0%'>
                            <stop offset='0%' stopColor='#A855F7' />
                            <stop offset='76.382%' stopColor='#FAF5FF' />
                            <stop offset='100%' stopColor='#6366F1' />
                          </linearGradient>
                        </defs>
                        <g fillRule='evenodd'>
                          <path
                            className='pulse'
                            fill='url(#pulse-a)'
                            fillRule='evenodd'
                            d='M240,0 C372.5484,0 480,107.4516 480,240 C480,372.5484 372.5484,480 240,480 C107.4516,480 0,372.5484 0,240 C0,107.4516 107.4516,0 240,0 Z M240,88.8 C156.4944,88.8 88.8,156.4944 88.8,240 C88.8,323.5056 156.4944,391.2 240,391.2 C323.5056,391.2 391.2,323.5056 391.2,240 C391.2,156.4944 323.5056,88.8 240,88.8 Z'
                          />
                          <path
                            className='pulse pulse-1'
                            fill='url(#pulse-a)'
                            fillRule='evenodd'
                            d='M240,0 C372.5484,0 480,107.4516 480,240 C480,372.5484 372.5484,480 240,480 C107.4516,480 0,372.5484 0,240 C0,107.4516 107.4516,0 240,0 Z M240,88.8 C156.4944,88.8 88.8,156.4944 88.8,240 C88.8,323.5056 156.4944,391.2 240,391.2 C323.5056,391.2 391.2,323.5056 391.2,240 C391.2,156.4944 323.5056,88.8 240,88.8 Z'
                          />
                          <path
                            className='pulse pulse-2'
                            fill='url(#pulse-a)'
                            fillRule='evenodd'
                            d='M240,0 C372.5484,0 480,107.4516 480,240 C480,372.5484 372.5484,480 240,480 C107.4516,480 0,372.5484 0,240 C0,107.4516 107.4516,0 240,0 Z M240,88.8 C156.4944,88.8 88.8,156.4944 88.8,240 C88.8,323.5056 156.4944,391.2 240,391.2 C323.5056,391.2 391.2,323.5056 391.2,240 C391.2,156.4944 323.5056,88.8 240,88.8 Z'
                          />
                        </g>
                      </svg>
                      {/* Grid */}
                      <div className='absolute inset-0 left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none w-[500px] h-[500px] rounded-full overflow-hidden [mask-image:_radial-gradient(black,_transparent_60%)]'>
                        <div className='h-[200%] animate-endless'>
                          <div className='absolute inset-0 [background:_repeating-linear-gradient(transparent,_transparent_48px,_theme(colors.white)_48px,_theme(colors.white)_49px)] blur-[2px] opacity-20' />
                          <div className='absolute inset-0 [background:_repeating-linear-gradient(transparent,_transparent_48px,_theme(colors.purple.500)_48px,_theme(colors.purple.500)_49px)]' />
                          <div className='absolute inset-0 [background:_repeating-linear-gradient(90deg,transparent,_transparent_48px,_theme(colors.white)_48px,_theme(colors.white)_49px)] blur-[2px] opacity-20' />
                          <div className='absolute inset-0 [background:_repeating-linear-gradient(90deg,transparent,_transparent_48px,_theme(colors.purple.500)_48px,_theme(colors.purple.500)_49px)]' />
                        </div>
                      </div>
                      {/* Icons */}
                      <AnimatePresence>
                        {Object.keys(icons).map((key) => {
                          const Icon = icons[parseInt(key) as keyof typeof icons];
                          return (
                            tab === parseInt(key) && (
                              <motion.div
                                key={key}
                                initial={{ opacity: 0, scale: 0.5, rotate: 90 }}
                                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                exit={{ opacity: 0, scale: 0, rotate: -90 }}
                                className='absolute flex items-center justify-center'
                                style={{
                                  fontSize: "3rem",
                                  background: "linear-gradient(to right, #512da8, #172547)",
                                  borderRadius: "20%",
                                  width: "5rem",
                                  height: "5rem",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Icon />
                              </motion.div>
                            )
                          );
                        })}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Botão Teste Grátis */}
            <div className='flex justify-center mt-16'>
              <ButtonFreeTrial />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
